import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, HandCoins, ArrowDown, ArrowUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CashBoxPicker from "@/components/CashBoxPicker";

export default function Vouchers() {
  const { currentCompany } = useAuth();
  const cur = currentCompany?.base_currency || "SYP";
  const [tab, setTab] = useState("receipt");
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ party_id:"", party_type:"customer", amount:0, currency:cur, method:"cash", notes:"", cash_box_id: null });

  const load = async () => {
    try {
      const [v, p] = await Promise.all([
        api.get("/vouchers", { params: { kind: tab } }),
        api.get(tab === "receipt" ? "/customers" : "/suppliers"),
      ]);
      setItems(v.data); setParties(p.data);
    } catch(e){ toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [tab, currentCompany?.id]); // eslint-disable-line

  const openNew = () => {
    setForm({ party_id:"", party_type: tab === "receipt" ? "customer" : "supplier", amount:0, currency:cur, method:"cash", notes:"", cash_box_id: null });
    setOpen(true);
  };

  const save = async () => {
    if (!form.party_id) return toast.error(tab === "receipt" ? "اختر عميلاً" : "اختر مورداً");
    if (+form.amount <= 0) return toast.error("المبلغ يجب أن يكون موجباً");
    try {
      await api.post("/vouchers", { ...form, kind: tab, amount: +form.amount });
      toast.success(`تم حفظ سند ${tab === "receipt" ? "قبض" : "صرف"}`);
      setOpen(false); load();
    } catch(e){ toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف السند؟ سيتم تصحيح رصيد الطرف والصندوق تلقائياً.")) return;
    try { await api.delete(`/vouchers/${id}`); toast.success("تم الحذف"); load(); } catch(e){ toast.error(formatErr(e)); }
  };

  const total = items.reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">سندات القبض والصرف</h1>
        <p className="text-sm text-muted-foreground mt-1">تسجيل دفعات العملاء وسداد الموردين — يُحدّث رصيد الطرف والصندوق تلقائياً</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="receipt" data-testid="tab-receipt"><ArrowDown className="w-4 h-4 me-1 text-emerald-600" /> سندات قبض (وارد)</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment"><ArrowUp className="w-4 h-4 me-1 text-destructive" /> سندات صرف (صادر)</TabsTrigger>
        </TabsList>
        <TabsContent value={tab}>
          <div className="flex items-center justify-between mb-3">
            <Card className="p-3 inline-block">
              <span className="text-xs text-muted-foreground">إجمالي {tab === "receipt" ? "المقبوضات" : "المدفوعات"}: </span>
              <span className="font-heading font-bold text-lg tabular-nums text-primary" data-testid="voucher-total">{fmtMoney(total, cur)}</span>
            </Card>
            <Button onClick={openNew} data-testid="add-voucher-button"><Plus className="w-4 h-4 me-1" /> سند {tab === "receipt" ? "قبض" : "صرف"} جديد</Button>
          </div>

          <div className="border border-border rounded-md bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-start">رقم</TableHead>
                <TableHead className="text-start">التاريخ</TableHead>
                <TableHead className="text-start">{tab === "receipt" ? "العميل" : "المورد"}</TableHead>
                <TableHead className="text-start">المبلغ</TableHead>
                <TableHead className="text-start">الوسيلة</TableHead>
                <TableHead className="text-start">ملاحظات</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد سندات</TableCell></TableRow>}
                {items.map(v => (
                  <TableRow key={v.id} data-testid={`voucher-row-${v.id}`}>
                    <TableCell className="font-mono">#{v.number}</TableCell>
                    <TableCell className="text-xs">{new Date(v.created_at).toLocaleString("ar-EG")}</TableCell>
                    <TableCell className="font-semibold">{v.party_name}</TableCell>
                    <TableCell className={`tabular-nums font-bold ${tab === "receipt" ? "text-emerald-600" : "text-destructive"}`}>
                      {fmtMoney(v.amount, v.currency)}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs px-2 py-0.5 rounded bg-muted">
                        {v.method === "cash" ? "نقدي" : v.method === "bank" ? "تحويل بنكي" : "شيك"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{v.notes || "—"}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={()=>del(v.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="voucher-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {tab === "receipt" ? <Receipt className="w-5 h-5 text-emerald-600" /> : <HandCoins className="w-5 h-5 text-destructive" />}
              سند {tab === "receipt" ? "قبض" : "صرف"} جديد
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>{tab === "receipt" ? "العميل" : "المورد"}</Label>
              <Select value={form.party_id} onValueChange={(v)=>setForm({...form, party_id:v})}>
                <SelectTrigger data-testid="voucher-party-select"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent>
                  {parties.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — رصيده: {fmtMoney(p.balance||0, cur)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>المبلغ</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid="voucher-amount-input" className="tabular-nums font-bold text-lg" /></div>
            <div className="p-2 rounded border bg-primary/5">
              <CashBoxPicker
                value={{ currency: form.currency, cash_box_id: form.cash_box_id }}
                onChange={(patch) => setForm({ ...form, ...patch })}
                company={currentCompany}
                label={tab === "receipt" ? "المبلغ سيدخل إلى:" : "المبلغ سيخرج من:"}
                data-testid="voucher-box"
              />
            </div>
            <div>
              <Label>الوسيلة</Label>
              <Select value={form.method} onValueChange={(v)=>setForm({...form, method:v})}>
                <SelectTrigger data-testid="voucher-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">نقدي</SelectItem>
                  <SelectItem value="bank">تحويل بنكي</SelectItem>
                  <SelectItem value="cheque">شيك</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>ملاحظات</Label><Input value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-voucher-button">حفظ السند</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
